import react, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';
import CardElement from '../Payments/CardElement';
import '../../Styles/Auctions/auctionItemViewStyle.css';

import { useAuctionData, enrollUser } from './AuctionData';
import useRoundCountdown from './RoundCountdown';
import PhotoCarousel from './PhotoCarousel';
import { NextBtnIcon, PrevBtnIcon } from '../Utils/Icons';
import formatCurrency from '../Utils/TextFormat';
import { DateTime } from 'luxon';

const auctionDb = "AuctionItems"
// const auctionDb = "TestAuctionItems"

function AuctionItemView() {
    const { id } = useParams();
    const [auctionItem, setAuctionItem] = useState(null);
    const [nextRoundCountdown, setNextRoundCountdown] = useState(null);
    const [roundDateTime, setRoundDateTime] = useState('');
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [amountToPay, setAmountToPay] = useState(0); 
    const [stripePromise, setStripePromise] = useState(null);
    const [userBidAmount, setUserBidAmount] = useState(0);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [showCardElement, setShowCardElement] = useState(false);
    const [downPaymentPercentage, setDownPaymentPercentage] = useState("");
    const [user, setUser] = useState(null);

    const auth = getAuth();
    const db = getFirestore();
    const totalRounds = auctionItem?.Rounds && typeof auctionItem.Rounds === 'object'
        ? Object.keys(auctionItem.Rounds).length
        : 0;
        
    const paymentMethods = ['Card Payment', 'Cash & Finance', 'Cash', 'Cashiers Check', 'Bank Transfer'];

    const toNY = (isoString) => {
        if (!isoString) return null;

        // Remove any offset (-04:00 or -05:00)
        const cleaned = isoString.replace(/([+-]\d{2}:\d{2})$/, '');

        // Parse without offset and treat as New York local time
        return DateTime.fromISO(cleaned, { setZone: false }).setZone('America/New_York', { keepLocalTime: true });
    };

    const displayESTTime = (isoString) => {
        if (!isoString) return '';

        // Remove any offset (-04:00 or -05:00)
        const cleaned = isoString.replace(/([+-]\d{2}:\d{2})$/, '');

        // Parse without offset and treat as New York local time (keep wall-clock time)
        const dt = DateTime.fromISO(cleaned, { setZone: false }).setZone('America/New_York', { keepLocalTime: true });

        return dt.toFormat('MM/dd/yyyy hh:mm a') + ' EST';
    };

    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged(setUser);
        const unsubscribeAuction = onSnapshot(doc(db, auctionDb, id), (docSnap) => {
            if (docSnap.exists()) {
                setAuctionItem(docSnap.data());
            }
        });

        return () => {
            unsubscribeAuth();
            unsubscribeAuction();
        };
    }, [auth, db, id]); 

    useEffect(() => {
        const fetchAuctionitem = async () => {
            try {
                const auctionDocRef = doc(db, auctionDb, id);
                const auctionDoc = await getDoc(auctionDocRef);
                if (auctionDoc.exists()) {
                    const auctionData = auctionDoc.data();
                    setAuctionItem(auctionDoc.data());

                    if (auctionData.EnrolledUsers && user && auctionData.EnrolledUsers.includes(user.uid)) {
                        setIsEnrolled(true);
                    }
                }
            } catch (error) {
                console.log("Error fetching auction item: ", error)
            }
        }

        fetchAuctionitem();
    }, [db, id, user]);

    useEffect(() => {
        const calculateRoundMinimums = () => {
            if (!auctionItem || !user) return;
        
            let totalMinimum = 0;
            let currentRoundIndex = -1;
        
            const rounds = Object.entries(auctionItem.Rounds || {}).sort(
                ([, a], [, b]) => toNY(a.Date)?.toMillis() - toNY(b.Date)?.toMillis()
            );
        
            const nowTs = DateTime.now().setZone('America/New_York').toMillis();
            let userAllowed = true;
        
            for (let i = 0; i < rounds.length; i++) {
                const [_, roundDetails] = rounds[i];
                const roundStartMs = toNY(roundDetails.Date)?.toMillis();
                const roundEndMs = toNY(roundDetails.endDate)?.toMillis();
        
                if (roundStartMs == null || roundEndMs == null) continue;

                if (nowTs >= roundStartMs && nowTs <= roundEndMs) {
                    currentRoundIndex = i;
                    break;
                }
            }
            if (currentRoundIndex === -1) {
                setAmountToPay(0);
                return;
            }
        
            let previousParticipation = false;
        
            for (let i = 0; i <= currentRoundIndex; i++) {
                const [roundId, roundDetails] = rounds[i];
        
                const hasUserParticipated = roundDetails.Participants?.some(
                    (participant) => participant.userId === user.uid
                );
        
                const minimumValue = parseFloat(roundDetails.Minimum) || 0;
        
                if (i === currentRoundIndex) {
                    if (i > 0 && !previousParticipation) {
                        userAllowed = false;
                    } else {
                        totalMinimum += minimumValue;
                    }
                } else {
                    previousParticipation = hasUserParticipated;
                }
            }
        
            if (!userAllowed) {
                alert("You cannot participate in this auction anymore as you didn't participate in the previous round.");
                return;
            }
        
            setAmountToPay(totalMinimum);
        };
    
        calculateRoundMinimums();

        const interval = setInterval(calculateRoundMinimums, 5000);

        return () => clearInterval(interval);
    }, [auctionItem, user]);

    useEffect(() => {
        if (!auctionItem) return;

        const calculateNextRoundCountdown = () => {
            const rounds = Object.entries(auctionItem.Rounds || {})
                .sort(([, a], [, b]) =>
                    toNY(a.Date)?.toMillis() - toNY(b.Date)?.toMillis()
                );

            const now = DateTime.now().setZone("America/New_York");
            const nowTs = now.toMillis();

            let currentRound = null;
            let nextRound = null;

            for (let i = 0; i < rounds.length; i++) {
                const [roundName, roundDetails] = rounds[i];

                const roundStart = toNY(roundDetails.Date);
                const roundEnd   = toNY(roundDetails.endDate);

                const startMs = roundStart?.toMillis();
                const endMs = roundEnd?.toMillis();

                if (startMs == null || endMs == null) continue;

                // If we're inside the round
                if (nowTs >= startMs && nowTs <= endMs) {
                    currentRound = { roundName, roundDetails, startMs, endMs };

                    if (i + 1 < rounds.length) {
                        const [nextName, nextDetails] = rounds[i + 1];
                        nextRound = { roundName: nextName, roundDetails: nextDetails };
                    }
                    break;
                }

                // If future round and no current round found yet
                if (!currentRound && nowTs < startMs) {
                    nextRound = { roundName, roundDetails, startMs };
                    break;
                }
            }

            if (currentRound) {
                const roundEndDT = toNY(currentRound.roundDetails.endDate);
                const diff = roundEndDT.diff(now, ["days", "hours", "minutes", "seconds"]).toObject();

                const nextRoundText = nextRound
                    ? ` | Next Round Begins (EST): ${displayESTTime(nextRound.roundDetails.Date)}`
                    : "";

                setRoundDateTime(
                    `Current Round Ends In: ${Math.floor(diff.days)}d ${Math.floor(diff.hours)}h ${Math.floor(diff.minutes)}m ${Math.floor(diff.seconds)}s${nextRoundText}`
                );

                setNextRoundCountdown({
                    roundName: currentRound.roundName,
                    countdown: null,
                    minAmount: currentRound.roundDetails.Minimum,
                    roundDetails: currentRound.roundDetails
                });

                return;
            }

            if (nextRound) {
                const roundStartDT = toNY(nextRound.roundDetails.Date);
                const diff = roundStartDT.diff(now, ["days", "hours", "minutes", "seconds"]).toObject();

                setRoundDateTime(
                    `Next Round: ${nextRound.roundName} | Time (EST): ${displayESTTime(nextRound.roundDetails.Date)}`
                );

                setNextRoundCountdown({
                    roundName: nextRound.roundName,
                    countdown: `${Math.floor(diff.days)} days, ${Math.floor(diff.hours)} hours, ${Math.floor(diff.minutes)} minutes, ${Math.floor(diff.seconds)} seconds`,
                    minAmount: nextRound.roundDetails.Minimum,
                    roundDetails: nextRound.roundDetails
                });

                return;
            }

            setNextRoundCountdown(null);
        };

        calculateNextRoundCountdown();
        const interval = setInterval(calculateNextRoundCountdown, 1000);
        return () => clearInterval(interval);
    }, [auctionItem]);

    const handleEnroll = async () => {
        try {
            const auctionDocRef = doc(db, auctionDb, id);
            const auctionDoc = await getDoc(auctionDocRef);
            
            if (!auctionDoc.exists()) {
                alert("Auction item not found.");
                return;
            }

            const auctionData = auctionDoc.data();

            if (auctionData.EnrolledUsers?.includes(user.uid)) {
                alert("You are already enrolled.");
                return;
            }

            await updateDoc(auctionDocRef, {
                EnrolledUsers: arrayUnion(user.uid),
            });

            setIsEnrolled(true);
            alert("You have successfully enrolled!");
        } catch (error) {
            alert("Error enrolling. Please try again.");
        }
    };

    useEffect(() => {
        const fetchStripe = async () => {
            try {
                const response = await fetch("https://fqzh94i59u.us-east-2.awsapprunner.com/config");
                const { publishableKey } = await response.json();
                const stripeInstance = await loadStripe(publishableKey);
                setStripePromise(stripeInstance);
            } catch (error) {
                alert("Failed to load payment system. Please refresh the page.");
            }
        };
        fetchStripe();
    }, []);

    const handleBuy = async () => {
        if (!user) {
            alert("You must be logged in to participate.");
            return;
        }

        const userDocRef = doc(db, 'Users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            console.log('User details not found.');
            return;
        }

        const userData = userDoc.data();
        if (!userData.AddressLine1 || !userData.Postcode || !userData.FirstName || !userData.LastName || !userData.PhoneNumber) {
            alert('Please update your Address Line 1, Zipcode, First Name, Last Name & phone number in your profile before submitting.');
            return;
        }

        const payment = amountToPay;
        const roundId = nextRoundCountdown.roundName;

        if (!stripePromise) {
            alert("Payment system is still loading. Please wait a moment.");
            return;
        }    
        
        try {
            const auctionDocRef = doc(db, auctionDb, id);
            const auctionDoc = await getDoc(auctionDocRef);

            if (!auctionDoc.exists()) {
                alert("Auction item not found.");
                return;
            }

            const auctionData = auctionDoc.data();
            const currentRound = auctionData.Rounds?.[roundId];
            const rounds = Object.entries(auctionData.Rounds || {}).sort(
                ([, a], [, b]) => toNY(a.Date)?.toMillis() - toNY(b.Date)?.toMillis()
            );    

            if (!currentRound) {
                alert("Current round not found.");
                return;
            }

            let currentRoundIndex = -1;
            const nowTs = DateTime.now().setZone('America/New_York').toMillis();

            for (let i = 0; i < rounds.length; i++) {
                const [roundName, roundDetails] = rounds[i];
                const roundStartDateMs = toNY(roundDetails.Date)?.toMillis();
                const roundEndDateMs = toNY(roundDetails.endDate)?.toMillis();

                if (roundStartDateMs == null || roundEndDateMs == null) continue;

                if (nowTs >= roundStartDateMs && nowTs <= roundEndDateMs) {
                    currentRoundIndex = i;
                    break;
                }
            }

            if (currentRoundIndex > 0) {
                const previousRound = rounds[currentRoundIndex - 1][1];
                const participatedInPreviousRound = previousRound.Participants?.some(
                    (participant) => participant.userId === user.uid
                );

                if (!participatedInPreviousRound) {
                    alert("You cannot participate in this auction anymore as you didn't participate in the previous round.");
                    return;
                }
            }

            const hasAlreadyPaidCurrentRound = currentRound.Participants?.some(participant => participant.userId === user.uid);

            if (hasAlreadyPaidCurrentRound) {
                alert("You have already paid for this round.");
                return;
            }

            setShowCardElement(true); 
        } catch (error) {
            alert("An error occurred. Please try again.");
        }
    };

    const handlePlaceBid = async () => {
        if (!user) {
            alert("You must be logged in to place a bid.");
            return;
        }

        const auctionDocRef = doc(db, auctionDb, id);
        const auctionDoc = await getDoc(auctionDocRef);
        const auctionData = auctionDoc.data();

        const roundId = nextRoundCountdown.roundName;
        const currentRound = auctionData.Rounds?.[roundId];
        const hasAlreadyBid = currentRound.Participants?.some(participant => participant.userId === user.uid);

        if (hasAlreadyBid) {
            alert("You have already placed a bid in this round.");
            return;
        }
    
        if (!userBidAmount || !selectedPaymentMethod) {
            alert("Please enter a bid amount and select a payment method.");
            return;
        }

        if (selectedPaymentMethod.toLowerCase().includes("finance") && !downPaymentPercentage) {
            alert("Please enter a down payment percentage.");
            return;
        }

        if (!auctionDoc.exists()) {
            alert("Auction item not found.");
            return;
        }
        
        const rounds = auctionData.Rounds ? Object.entries(auctionData.Rounds) : [];
    
        const sortedRounds = rounds.sort((a, b) => {
            const roundA = parseInt(a[0].replace('Round', ''));
            const roundB = parseInt(b[0].replace('Round', ''));
            return roundB - roundA;
        });
    
        const currentRoundName = nextRoundCountdown.roundName; 
        
        // Find the current round in the sorted rounds
        const currentRoundIndex = parseInt(currentRoundName.replace('Round', ''));
    
        if (currentRoundIndex < 1 || currentRoundIndex > rounds.length) {
            console.log("Invalid current round index.");
            return;
        }
    
        if (currentRoundIndex > 1) {
            const previousRoundIndex = currentRoundIndex - 1;
            const previousRoundName = 'Round' + previousRoundIndex;
            const previousRoundDetails = sortedRounds.find(round => round[0] === previousRoundName);
            const participants = previousRoundDetails[1].Participants || [];
    
            const hasParticipatedInPreviousRound = participants.some(participant => participant.userId === user.uid);
            
            if (!hasParticipatedInPreviousRound) {
                alert(`You must participate in all previous rounds.`);
                return; 
            }
        } else {
            console.log("It's Round 1, no previous round to check participation.");
        }
    
        if (!currentRound) {
            alert("Round not found.");
            return;
        }

        const downPay = selectedPaymentMethod.toLowerCase().includes("finance") ? downPaymentPercentage : 0;
    
        try {
            // Proceed with placing the bid
            await updateDoc(auctionDocRef, {
                [`Rounds.${currentRoundName}.Participants`]: arrayUnion({
                    userId: user.uid,
                    bidAmount: userBidAmount,
                    paymentMethod: selectedPaymentMethod,
                    downPaymentPercentage: downPay,
                })
            });
    
            alert("Your bid has been placed successfully!");
            await fetch("https://fqzh94i59u.us-east-2.awsapprunner.com/notifications/final-bid", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  to: user.email
                }),
            });
            setUserBidAmount(0);
            setSelectedPaymentMethod('');
        } catch (error) {
            alert("There was an error placing your bid. Please try again.");
        }
    };

    const determineButton = () => {
        if (!user) {
            return;
        }

        const nowTs = DateTime.now().setZone('America/New_York').toMillis();
        const rounds = auctionItem.Rounds ? Object.entries(auctionItem.Rounds) : [];
        let totalMinAmount = 0;
        let hasPendingRounds = false;
        
        const validRoundDates = rounds
            .map(([_, roundDetails]) => toNY(roundDetails.Date))
            .filter(dt => dt && !isNaN(dt.toMillis()))
            .map(dt => dt.toMillis());

        // Step 2: Find the earliest valid round start date
        const earliestRound = validRoundDates.length > 0
            ? new Date(Math.min(...validRoundDates))
            : null;
        
        
        if (earliestRound && nowTs < earliestRound.getTime()) {
            // Before the first round starts
            if (!isEnrolled) {
                return <button type="submit" onClick={handleEnroll}>Enroll</button>;
            }
        }
    
        for (const [roundName, roundDetails] of rounds) {
            const roundStartMs = toNY(roundDetails.Date)?.toMillis();
            const roundEndMs = toNY(roundDetails.endDate)?.toMillis();

            const minimumValue = parseFloat(roundDetails.Minimum) || 0;
    
            if (roundStartMs != null && roundEndMs != null && nowTs >= roundStartMs && nowTs <= roundEndMs) {
                const hasPaid = roundDetails.Participants?.some(participant => participant.userId === user.uid);
                if (hasPaid) {
                    return (
                        <button type="submit" className="paidBtn" disabled>
                            Paid
                        </button>
                    );
                } else {
                    return <button type="submit" onClick={handleBuy}>Pay</button>;
                }
            } else {
                if (!isEnrolled) {
                    totalMinAmount += minimumValue;
                    hasPendingRounds = true;
                }
            }
        }

        return <p></p>;
    };
    
    const determineText = () => {
        if (!auctionItem) return <p>No auction data available</p>;

        const nowTs = DateTime.now().setZone('America/New_York').toMillis();
        const rounds = Object.entries(auctionItem.Rounds || {}).sort(
            ([, a], [, b]) => toNY(a.Date)?.toMillis() - toNY(b.Date)?.toMillis()
        );

        let totalMinAmount = 0;
        let hasPendingRounds = false;
        let currentRound = null;
        let nextRound = null;

        const formatRoundName = (roundName) => {
            return roundName.replace(/Round(\d+)/, "Round $1");
        };

        for (let i = 0; i < rounds.length; i++) {
            const [roundName, roundDetails] = rounds[i];
            const roundStartDateMs = toNY(roundDetails.Date)?.toMillis();
            const roundEndDateMs = toNY(roundDetails.endDate)?.toMillis();
    
            if (roundStartDateMs == null || roundEndDateMs == null) continue;

            if (nowTs >= roundStartDateMs && nowTs <= roundEndDateMs) {
                currentRound = { roundName, roundDetails };
                break;
            }
    
            if (nowTs < roundStartDateMs && !nextRound) {
                nextRound = { roundName, roundDetails };
            }
    
            if (nowTs > roundEndDateMs && !isEnrolled) {
                const minimumValue = parseFloat(roundDetails.Minimum) || 0;
                totalMinAmount += minimumValue;
            }
        }    
        
        if (currentRound) {
            return (
                <p>
                    Current Round: {currentRound.roundName}
                </p>
            );
        }     
        
        if (nextRound) {
            return (
                <p>
                    Time until next round
                </p>
            );
        }
        
        return <p>No active rounds</p>;
    };

    const formatCurrencyInput = (value) => {
        if (!value) return "";
        const numericValue = value.replace(/[^0-9.]/g, ""); // keep only numbers + dot
        const parsed = parseFloat(numericValue);
        if (isNaN(parsed)) return "";
        return parsed.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
    };
      
    const handleBidChange = (e) => {
        const rawValue = e.target.value;
        const formatted = formatCurrencyInput(rawValue);
        setUserBidAmount(formatted);
    };      

    if (!auctionItem) {
        return <p>Loading auction...</p>;
    }

    return (
        <div className="auctionItemViewContainer">
            <p className="category">Auctions / <span className="auctionCategory">{auctionItem.Category}</span></p>
            <h1>{auctionItem.Name}</h1>
            <div className="images">
                {auctionItem.Photos?.length > 0 && (
                    <PhotoCarousel photos={auctionItem.Photos || []} />
                )}
            </div>
            <div className="itemContent">
                <h2 className="subHeading">Auction Details</h2>
                <div className="description" dangerouslySetInnerHTML={{ __html: auctionItem.Description }} />
                <div className="cardElementContainer" style={{ display: showCardElement ? 'block' : 'none' }}>
                    <CardElement
                        onSubmit={() => {setShowCardElement(false)}}
                        onCancel={() => {setShowCardElement(false)}}
                        userId={user?.uid} 
                        amount={amountToPay} 
                        roundId={nextRoundCountdown?.roundName} 
                        auctionId={id} 
                        auctionName={auctionItem.Name}
                    />
                </div>
                <div className="roundInfoContainer">
                    <h2 className="subHeading">Bidding Info</h2>
                    {auctionItem?.Rounds && 
                        Object.values(auctionItem.Rounds).every(r => !r.Date || r.Date.trim() === '') ? (
                        <div className="nextRoundCountdown">
                            <h2>Coming soon...</h2>
                            {isEnrolled ? (
                                <button disabled={true} type="submit">Enrolled</button>
                            ) : (
                                <button type="submit" onClick={handleEnroll}>Enroll</button>
                            )}
                        </div>
                    ) : nextRoundCountdown ? (
                        <div className="nextRoundCountdown">
                            <p>{determineText()}</p>
                            <h2>
                                {nextRoundCountdown?.roundName && parseInt(nextRoundCountdown.roundName.replace('Round', '')) >= totalRounds ? (
                                    nextRoundCountdown.countdown
                                ) : (
                                    nextRoundCountdown?.countdown ? nextRoundCountdown.countdown : `${formatCurrency(amountToPay)}`
                                )}
                            </h2>
                            <p>{roundDateTime}</p>
                            
                            {user ? (
                                <div className="roundInteractions">
                                    {(() => {
                                        const nowTs = DateTime.now().setZone('America/New_York').toMillis();
                                        const roundStartMs = toNY(nextRoundCountdown.roundDetails.Date)?.toMillis();
                                        const roundEndMs = toNY(nextRoundCountdown.roundDetails.endDate)?.toMillis();
                                        const currentRoundParticipants = nextRoundCountdown.roundDetails.Participants || [];
                                        const hasBid = currentRoundParticipants.some(participant => participant.userId === user?.uid);

                                        if (nextRoundCountdown.roundName.startsWith("Round") && parseInt(nextRoundCountdown.roundName.replace('Round', '')) === totalRounds && roundStartMs != null && roundEndMs != null && nowTs > roundStartMs && nowTs < roundEndMs) {
                                            return (
                                                <div className="bestFinalContainer">
                                                    <label>
                                                        Bid Amount
                                                        <input
                                                            type="text"
                                                            placeholder="Enter your best bid"
                                                            value={userBidAmount}
                                                            onChange={handleBidChange}
                                                        />
                                                    </label>
                                                    {selectedPaymentMethod.toLowerCase().includes("finance") && (
                                                        <label>
                                                            Down Payment %
                                                            <input
                                                                type="number"
                                                                placeholder="Down payment %"
                                                                value={downPaymentPercentage}
                                                                onChange={(e) => setDownPaymentPercentage(e.target.value)}
                                                                min="0"
                                                                max="100"
                                                            />
                                                        </label>
                                                    )}
                                                    <label>
                                                        Payment Method
                                                        <select
                                                            value={selectedPaymentMethod}
                                                            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                                            required
                                                        >
                                                            <option value="">All Payment Methods</option>
                                                            {paymentMethods.map((item) => (
                                                                <option key={item} value={item}>
                                                                    {item}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <button onClick={handlePlaceBid} type="submit">
                                                        {hasBid ? "Bid Submitted" : "Submit Bid"}
                                                    </button>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div className="actionButton">
                                                    {determineButton()}
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            ) : (
                                <p>Please login to interact with the auction.</p>
                            )}
                        </div>
                    ) : (
                        <p>All rounds have concluded.</p>
                    )}
                </div>
                <h2 className="subHeading">Terms & Shipping</h2>
                <div className="dealTerms" dangerouslySetInnerHTML={{ __html: auctionItem.DealTerms }} />
            </div>

            <div className="overlay" style={{ display: showCardElement ? 'block' : 'none' }}></div>
        </div>
    )
}

export default AuctionItemView;
